import React from "react";
import { ShieldAlert, AlertTriangle, ShieldCheck } from "lucide-react";

export function ForensicReport({ order }) {
  const forensic = order?.verificationLog?.extracted?.forensic;
  if (!forensic || !forensic.integrity_check) return null;

  const status = String(forensic.integrity_check.status || "").toUpperCase();
  const risk = String(forensic.risk_assessment?.risk_level || "").toUpperCase();
  const recommendation = String(forensic.risk_assessment?.recommendation || "").toUpperCase();
  const confidence = Number(forensic.integrity_check.confidence_score ?? 0);
  const flags = Array.isArray(forensic.flags) ? forensic.flags : [];

  const isBad = status === "FAIL" || status === "SUSPICIOUS" || risk === "HIGH" || recommendation === "REJECT";
  const isWarn = !isBad && (risk === "LOW" || recommendation === "REVIEW_MANUALLY");

  const palette = isBad
    ? { bg: "from-red-950/50 to-red-900/20", border: "border-red-500/50", text: "text-red-100", label: "text-red-300", icon: ShieldAlert, iconColor: "text-red-400", badgeCls: "bg-red-500/20 text-red-200 border-red-500/40" }
    : isWarn
    ? { bg: "from-amber-950/40 to-amber-900/20", border: "border-amber-500/40", text: "text-amber-100", label: "text-amber-300", icon: AlertTriangle, iconColor: "text-amber-400", badgeCls: "bg-amber-500/20 text-amber-200 border-amber-500/40" }
    : { bg: "from-emerald-950/40 to-emerald-900/10", border: "border-emerald-500/30", text: "text-emerald-100", label: "text-emerald-300", icon: ShieldCheck, iconColor: "text-emerald-400", badgeCls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" };
  const Icon = palette.icon;

  return (
    <div
      data-testid={`forensic-report-${order.id}`}
      className={`p-4 rounded-2xl bg-gradient-to-br ${palette.bg} border ${palette.border} space-y-3`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-black/40 border border-white/10 flex items-center justify-center">
            <Icon className={`w-4 h-4 ${palette.iconColor}`} />
          </div>
          <div>
            <p className={`text-[10px] font-mono uppercase tracking-widest ${palette.label}`}>
              Análisis forense IA
            </p>
            <p className={`text-sm font-bold ${palette.text}`}>
              Integridad: <span className="font-mono">{status || "N/D"}</span>
              {" · "}Riesgo: <span className="font-mono">{risk || "N/D"}</span>
            </p>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${palette.badgeCls}`} data-testid={`forensic-recommendation-${order.id}`}>
          {recommendation || "SIN DICTAMEN"} · {confidence}%
        </span>
      </div>

      {flags.length > 0 && (
        <div className="space-y-1.5">
          <p className={`text-[10px] font-mono uppercase tracking-widest ${palette.label}`}>
            Hallazgos ({flags.length})
          </p>
          <ul className={`text-xs ${palette.text} space-y-1 list-disc list-inside`}>
            {flags.map((f, i) => (
              <li key={`${i}-${String(f).slice(0, 24)}`} className="leading-snug">{String(f)}</li>
            ))}
          </ul>
        </div>
      )}

      {forensic.integrity_check.verification_details && (
        <details data-testid="forensic-report-details-1" className="text-[11px]">
          <summary data-testid="forensic-report-summary-2" className={`cursor-pointer ${palette.label} font-mono uppercase tracking-widest text-[10px]`}>
            Detalles técnicos
          </summary>
          <div className={`mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 font-mono ${palette.text}`}>
            {Object.entries(forensic.integrity_check.verification_details).map(([k, v]) => (
              <div key={k} className="truncate">
                <span className="opacity-60">{k}:</span> {String(v)}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
